/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useMemo, useState, useRef } from "react";
import {
  Box,
  Text,
  Select,
  Stack,
  Spinner,
  Button,
  Alert,
  AlertIcon,
  Flex,
  FormControl,
  Input,
  useColorModeValue,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  useDisclosure,
} from "@chakra-ui/react";
import { SearchIcon, ChevronDownIcon } from "@chakra-ui/icons";

const normalizeSearchText = (value = "") =>
  String(value)
    .toLocaleLowerCase("tr-TR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .trim();

const SearchableFirmSelect = ({
  firms = [],
  selectedFirmId,
  onChange,
  isDisabled,
  includeAllOption = false,
  allLabel = "Hepsi",
  allValue = "all",
  placeholder = "Firma Seçiniz",
}) => {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const bg = useColorModeValue("white", "gray.700");

  // Options oluştur
  const options = useMemo(() => {
    const opts = [];
    if (includeAllOption) {
      opts.push({ label: allLabel, value: allValue });
    }
    firms.forEach((firma) => {
      opts.push({
        label: firma.name || firma.firma_ismi || firma.firma,
        value: firma.id,
      });
    });
    return opts;
  }, [firms, includeAllOption, allLabel, allValue]);

  const filteredOptions = useMemo(() => {
    if (!query) return options;
    const normalizedQuery = normalizeSearchText(query);
    return options.filter((opt) =>
      normalizeSearchText(opt.label).includes(normalizedQuery)
    );
  }, [options, query]);

  // Seçili değerin label'ını bul
  const selectedLabel = selectedFirmId
    ? options.find((opt) => String(opt.value) === String(selectedFirmId))?.label || placeholder
    : placeholder;

  return (
    <FormControl w={{ base: "100%", md: "150px" }} isDisabled={isDisabled}>
      <Popover
        placement="bottom-start"
        isLazy
        matchWidth
        isOpen={isOpen}
        initialFocusRef={inputRef}
        onClose={() => {
          setQuery("");
          onClose();
        }}
      >
        <PopoverTrigger>
          <Box
            as="button"
            type="button"
            w="100%"
            px={3}
            py={1.5}
            fontSize="sm"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            bg="white"
            color={selectedFirmId ? "gray.800" : "gray.500"}
            textAlign="left"
            cursor={isDisabled ? "not-allowed" : "pointer"}
            opacity={isDisabled ? 0.6 : 1}
            _hover={isDisabled ? {} : { borderColor: "blue.300" }}
            _focus={isDisabled ? {} : { borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
            disabled={isDisabled}
            onClick={isDisabled ? undefined : onOpen}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text isTruncated flex={1} mr={2}>
              {selectedLabel}
            </Text>
            <ChevronDownIcon />
          </Box>
        </PopoverTrigger>
        <PopoverContent w="240px" _focus={{ boxShadow: "lg" }}>
          <PopoverBody>
            <Input
              ref={inputRef}
              size="sm"
              placeholder="Ara..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              mb={2}
              autoFocus
            />
            <Box maxH="200px" overflowY="auto" bg={bg} borderRadius="md">
              {filteredOptions.map((opt) => (
                <Button
                  key={opt.value}
                  variant="ghost"
                  size="sm"
                  w="100%"
                  justifyContent="flex-start"
                  onClick={() => {
                    onChange?.(opt.value);
                    setQuery("");
                    onClose();
                  }}
                >
                  {opt.label}
                </Button>
              ))}
              {!filteredOptions.length && (
                <Box px={2} py={1} fontSize="sm" color="gray.500">
                  Sonuç bulunamadı
                </Box>
              )}
            </Box>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </FormControl>
  );
};

const FieldRenderer = ({ field, onSearch, onKeyPress }) => {
  const {
    type = "text",
    label,
    placeholder,
    value,
    onChange,
    options = [],
    isDisabled,
  } = field;

  if (type === "select") {
    return (
      <FormControl w={{ base: "100%", md: "130px" }}>
        <Select
          size="sm"
          placeholder={placeholder || label}
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          isDisabled={isDisabled}
          fontSize="sm"
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <FormControl w={{ base: "100%", md: "130px" }}>
      <Input
        size="sm"
        placeholder={placeholder || label}
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        onKeyPress={(e) => {
          if (e.key === "Enter") {
            if (onKeyPress) {
              onKeyPress(e);
            } else if (onSearch) {
              onSearch();
            }
          }
        }}
        isDisabled={isDisabled}
        fontSize="sm"
      />
    </FormControl>
  );
};

function FirmSelectorWithFilters({
  // FirmSelector props
  title,
  description,
  firms = [],
  selectedFirmId,
  onChange,
  loading: firmsLoading,
  error: firmsError,
  includeAllOption = false,
  allLabel = "Hepsi",
  allValue = "all",
  
  // FilterPanel props
  fields = [],
  actions = [], // Ekstra action butonları
  onSearch, // "Listele" butonu için callback
  onReset,
  searchButtonLabel = "Listele",
  searchButtonLoading = false,
  searchButtonLoadingText = "Yükleniyor...", // Loading durumunda gösterilecek metin
  onKeyPress, // Enter tuşu için callback
}) {
  const filterBg = useColorModeValue("gray.50", "gray.800");
  const filterBorder = useColorModeValue("gray.200", "gray.700");

  return (
    <Box
      bg="white"
      borderRadius="2xl"
      boxShadow="md"
      px={{ base: 4, md: 6 }}
      py={4}
      mb={4}
    >
      {/* Filtreler Bölümü - Tek bütün */}
      <Stack spacing={3}>

        {firmsLoading && (
          <Stack direction="row" align="center" spacing={2} color="gray.500">
            <Spinner size="sm" />
            <Text fontSize="sm">Firmalar yükleniyor...</Text>
          </Stack>
        )}

        {firmsError && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Text fontSize="sm">{firmsError}</Text>
          </Alert>
        )}

        {/* Filtreler Bölümü - Her zaman görünür */}
        <Box
          px={3}
          py={2.5}
          bg={filterBg}
          borderRadius="lg"
          border="1px solid"
          borderColor={filterBorder}
        >
          <Flex
            wrap="wrap"
            gap={2}
            align="flex-end"
            justify="flex-start"
          >
            {/* Firma Seçimi - Searchable Select */}
            <SearchableFirmSelect
              firms={firms}
              selectedFirmId={selectedFirmId}
              onChange={onChange}
                isDisabled={firmsLoading || !!firmsError}
              includeAllOption={includeAllOption}
              allLabel={allLabel}
              allValue={allValue}
              placeholder={firmsLoading ? "Firmalar yükleniyor..." : "Firma Seçiniz"}
            />

            {fields.map((field) => (
              <FieldRenderer key={field.key} field={field} onSearch={onSearch} onKeyPress={onKeyPress} />
            ))}

            {/* Listele/Ara Butonu */}
            {onSearch && (
              <FormControl w={{ base: "100%", md: "auto" }}>
                <Button
                  size="sm"
                  colorScheme="blue"
                  leftIcon={<SearchIcon />}
                  onClick={onSearch}
                  isLoading={searchButtonLoading}
                  loadingText={searchButtonLoadingText}
                >
                  {searchButtonLabel}
                </Button>
              </FormControl>
            )}

            {/* Sıfırla Butonu */}
            {onReset && (
              <FormControl w={{ base: "100%", md: "auto" }}>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  onClick={onReset}
                >
                  Sıfırla
                </Button>
              </FormControl>
            )}

            {/* Ekstra Action Butonları */}
            {actions.map((action) => (
              <FormControl
                key={action.key || action.label}
                w={{ base: "100%", md: "auto" }}
              >
                <Button
                  size="sm"
                  {...action.buttonProps}
                  onClick={action.onClick}
                >
                  {action.label}
                </Button>
              </FormControl>
            ))}
          </Flex>
        </Box>
      </Stack>
    </Box>
  );
}

export default React.memo(FirmSelectorWithFilters);

